import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useNodePerformanceContext } from '@lyearn/lygo-core';
import { Box } from '@lyearn/ui';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { PerformanceStatusEnum } from '@/types/schema';

import { QuizResultParams } from '../../../quiz/views/QuizPlayer/types';
import { useCourse } from '../../courseContext';
import CourseEntity from '../../entity/Course';
import QuizContainer from './components/QuizContainer';
import QuizEditorModal from './components/QuizEditorModal';
import Search from './components/Search';
import { QuizContextProvider } from './context/QuizBlockContext';
import { QuizCardContainerProps } from './types';

function QuizCardContainer(props: QuizCardContainerProps) {
  const { contentId, updateAttribute, editable, duration, id, inlinePlayer } = props;
  const user = useUserContext();
  const { setNodePerformance, getNodePerformance } = useNodePerformanceContext();
  const { course } = useCourse();
  const [showEditorModal, { on: openEditorModal, off: closeEditorModal }] = useToggle(false);

  const onLoad = React.useCallback(
    (content: any) => {
      if (editable && content.duration && content.duration !== duration) {
        updateAttribute({ duration: content.duration });
      }
    },
    [duration, editable, updateAttribute],
  );

  const setQuizPerformance = React.useCallback(
    ({ performanceStatus }: QuizResultParams) => {
      if (!id) {
        return;
      }
      const newProgress = performanceStatus === PerformanceStatusEnum.Completed ? 100 : 0;
      if (newProgress !== getNodePerformance?.(id)?.progress) {
        setNodePerformance?.({ id, blockType: 'quiz', progress: newProgress });
      }
    },
    [getNodePerformance, id, setNodePerformance],
  );

  React.useEffect(() => {
    // set performance for empty block where quiz is still to be attach
    if (!editable && !contentId && !duration) {
      setQuizPerformance({ performanceStatus: PerformanceStatusEnum.Completed });
    }
  }, [duration, editable, contentId, setQuizPerformance]);

  const renderContentSearch = () => {
    if (!editable) {
      return null;
    }

    return <Search updateAttribute={updateAttribute} onCreateNew={openEditorModal} />;
  };

  if (!CourseEntity.id(course)) {
    return null;
  }

  return (
    <QuizContextProvider quizBlockId={id}>
      <Box className="mt-24">
        {contentId ? (
          <QuizContainer
            contentId={contentId}
            courseId={CourseEntity.id(course)}
            inlinePlayer={inlinePlayer}
            quizId={contentId}
            userId={user.id}
            onEditQuiz={editable ? openEditorModal : undefined}
            onLoad={onLoad}
            onQuizSubmit={setQuizPerformance}
          />
        ) : (
          renderContentSearch()
        )}
      </Box>
      {editable ? (
        <QuizEditorModal
          contentId={contentId}
          open={showEditorModal}
          updateAttribute={updateAttribute}
          onClose={closeEditorModal}
        />
      ) : null}
    </QuizContextProvider>
  );
}

export default QuizCardContainer;
